import { classNames } from '@dimjs/utils';
import { flatbizDate } from '@flatbiz/utils';
import { hooks } from '@wove/react';
import { useRef, useState } from 'react';
import { useEffectCustom } from '../hooks/use-effect-custom';
import { Picker, PickerColumnItem, PickerColumnValue } from '../picker';
import './styles/date-view.less';
import {
  getDayList,
  getMonthList,
  getYearList,
  latelyDateItemValue,
  targetValueInDateItemList,
} from './utils';

export interface DateViewProps {
  minDate: string;
  maxDate: string;
  defaultDate: string;
  value?: string;
  onChange?: (pickerColumnItem: PickerColumnItem<string>) => void;
  itemHeight?: number;
  className?: string;
}

/**
 * 选择日期
 * 1. 如果minDate、maxDate数据有异常，则系统为minDate、maxDate重新赋值，赋值规则为currentDate的前后两年
 *
 * https://stackoverflow.com/questions/4310953/invalid-date-in-safari
 */
export const DateView = (props: DateViewProps) => {
  const currentDateRef = useRef('');
  const minDateRef = useRef('');
  const maxDateRef = useRef('');

  const [yearList, setYearList] = useState<PickerColumnItem[]>([]);
  const [monthList, setMonthList] = useState<PickerColumnItem[]>([]);
  const [dayList, setDyList] = useState<PickerColumnItem[]>([]);
  const [selectedValueList, setSelectedValueList] = useState<PickerColumnValue[]>([]);

  const getMinMaxDate = hooks.useCallbackRef(() => {
    return { minDate: minDateRef.current, maxDate: maxDateRef.current };
  });

  useEffectCustom(() => {
    let currentDate = props.value || props.defaultDate;
    let minDate = props.minDate;
    let maxDate = props.maxDate;
    if (!flatbizDate.isDate(currentDate)) {
      currentDate = flatbizDate.format(new Date(), 'YYYY-MM-DD');
    } else {
      currentDate = flatbizDate.format(currentDate, 'YYYY-MM-DD');
    }
    // 如果minDate、maxDate数据有异常，则系统为minDate、maxDate重新赋值，赋值规则为currentDate的前后两年
    if (!flatbizDate.isDate(minDate)) {
      minDate = flatbizDate.format(flatbizDate.update(currentDate, 'y', 2), 'YYYY-MM-DD');
    } else {
      minDate = flatbizDate.format(minDate, 'YYYY-MM-DD');
    }
    if (!flatbizDate.isDate(maxDate)) {
      maxDate = flatbizDate.format(flatbizDate.update(currentDate, 'y', -2), 'YYYY-MM-DD');
    } else {
      maxDate = flatbizDate.format(maxDate, 'YYYY-MM-DD');
    }
    if (flatbizDate.gt(currentDate, maxDate)) {
      console.warn(`当前指定时间【${currentDate}】大于最大设置时间【${maxDate}】`);
      currentDate = maxDate;
    }
    if (flatbizDate.gt(minDate, currentDate)) {
      console.warn(`当前指定时间【${currentDate}】小于最小设置时间【${minDate}】`);
      currentDate = minDate;
    }
    currentDateRef.current = currentDate;
    minDateRef.current = minDate;
    maxDateRef.current = maxDate;
    const yearList = getYearList(getMinMaxDate());
    setYearList(yearList);
    const { y, m, d } = flatbizDate.detail(currentDateRef.current);
    const monthList = getMonthList(y, getMinMaxDate());
    setMonthList(monthList);
    const dayList = getDayList(y, m, getMinMaxDate());
    setDyList(dayList);
    setSelectedValueList([y, m, d]);

    return () => {
      setYearList([]);
      setMonthList([]);
      setDyList([]);
      setSelectedValueList([]);
    };
  }, [props.value, props.maxDate, props.minDate]);

  const onYearChange = hooks.useCallbackRef((pickerItem: PickerColumnItem) => {
    const nextYearValue = Number(pickerItem.value);
    if (nextYearValue === selectedValueList[0]) {
      return;
    }
    const monthList = getMonthList(nextYearValue, getMinMaxDate());
    const currentMonthValue = Number(selectedValueList[1]);
    const nextMonthValue = targetValueInDateItemList(monthList, currentMonthValue)
      ? currentMonthValue
      : latelyDateItemValue(monthList, currentMonthValue);

    const dayList = getDayList(nextYearValue, nextMonthValue, getMinMaxDate());
    const currentDayValue = Number(selectedValueList[2]);
    const nextDayValue = targetValueInDateItemList(dayList, currentDayValue)
      ? currentDayValue
      : latelyDateItemValue(dayList, currentDayValue);

    const selectedDateItemList = [nextYearValue, nextMonthValue, nextDayValue];
    setMonthList(monthList);
    setDyList(dayList);
    setSelectedValueList(selectedDateItemList);
    const selectedDate = flatbizDate.format(selectedDateItemList.join('-'), 'YYYY-MM-DD');
    props.onChange?.({
      label: selectedDate,
      value: selectedDate,
    });
  });

  const onMonthChange = hooks.useCallbackRef((pickerItem: PickerColumnItem) => {
    const nextMonthValue = pickerItem.value as number;
    if (nextMonthValue === selectedValueList[1]) {
      return;
    }
    const dayList = getDayList(selectedValueList[0] as number, nextMonthValue, getMinMaxDate());
    const currentDayValue = Number(selectedValueList[2]);
    const nextDayValue = targetValueInDateItemList(dayList, currentDayValue)
      ? currentDayValue
      : latelyDateItemValue(dayList, currentDayValue);

    setDyList(dayList);
    const selectedDateItemList = [selectedValueList[0], nextMonthValue, nextDayValue];
    setSelectedValueList(selectedDateItemList);
    const selectedDate = flatbizDate.format(selectedDateItemList.join('-'), 'YYYY-MM-DD');
    props.onChange?.({
      label: selectedDate,
      value: selectedDate,
    });
  });

  const onDayChange = hooks.useCallbackRef((pickerItem: PickerColumnItem) => {
    const value = pickerItem.value;
    const selectedDateItemList = [selectedValueList[0], selectedValueList[1], value];
    setSelectedValueList(selectedDateItemList);
    const selectedDate = flatbizDate.format(selectedDateItemList.join('-'), 'YYYY-MM-DD');
    props.onChange?.({
      label: selectedDate,
      value: selectedDate,
    });
  });

  if (!currentDateRef.current || !minDateRef.current || !maxDateRef.current) {
    return null;
  }

  return (
    <div className={classNames('v-date-view', props.className)}>
      <Picker
        pickerDataList={yearList}
        onChange={onYearChange}
        value={selectedValueList[0]}
        itemHeight={props.itemHeight}
      />
      <Picker
        pickerDataList={monthList}
        onChange={onMonthChange}
        value={selectedValueList[1]}
        itemHeight={props.itemHeight}
      />
      <Picker
        pickerDataList={dayList}
        onChange={onDayChange}
        value={selectedValueList[2]}
        itemHeight={props.itemHeight}
      />
    </div>
  );
};
